import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Edit, Check, X, Plus, KeyRound } from "lucide-react";
import { useConfirm } from "../components/ConfirmDialog";
import { API } from "../lib/api";

const Usuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, confirmDialog] = useConfirm();
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    role: 'operacional',
    status: 'ativo',
    nova_senha: ''
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetSenha, setResetSenha] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsuarios(response.data);
    } catch (error) {
      toast.error("Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const carregar = async () => { await fetchData(); };
    carregar();
  }, []);

  const resetForm = () => {
    setFormData({
      nome: '',
      email: '',
      role: 'operacional',
      status: 'ativo',
      nova_senha: ''
    });
    setEditingId(null);
  };

  const handleNew = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const handleEdit = (usuario) => {
    setFormData({
      nome: usuario.nome,
      email: usuario.email,
      role: usuario.role,
      status: usuario.status,
      nova_senha: '' // Sempre vazio para edição, admin preenche se quiser alterar
    });
    setEditingId(usuario.id);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.email) {
      toast.error("Nome e email são obrigatórios");
      return;
    }

    try {
      if (editingId) {
        // Primeiro, atualizar dados básicos do usuário (sem senha)
        const userData = {
          nome: formData.nome,
          email: formData.email,
          role: formData.role,
          status: formData.status
        };
        
        await axios.put(`${API}/users/${editingId}`, userData);
        
        // Se uma nova senha foi fornecida, alterar senha separadamente
        if (formData.nova_senha && formData.nova_senha.trim() !== '') {
          await axios.put(`${API}/users/${editingId}/password`, {
            nova_senha: formData.nova_senha
          });
          toast.success("Usuário e senha atualizados com sucesso!");
        } else {
          toast.success("Usuário atualizado com sucesso!");
        }
      } else {
        // Criar novo usuário (POST /auth/register — admin only)
        if (!formData.nova_senha || formData.nova_senha.trim() === '') {
          toast.error("Senha é obrigatória para novo usuário");
          return;
        }
        await axios.post(`${API}/auth/register`, {
          nome: formData.nome,
          email: formData.email,
          senha: formData.nova_senha,
          role: formData.role,
        });
        toast.success("Usuário criado com sucesso!");
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 400 && typeof detail === 'string' && /email/i.test(detail)) {
        toast.error("Este email já está sendo usado por outro usuário");
      } else {
        toast.error(editingId ? "Erro ao atualizar usuário" : "Erro ao criar usuário");
      }
    }
  };

  const handleDeactivate = async (usuarioId) => {
    const ok = await confirm({
      title: "Desativar usuário?",
      description: "Tem certeza que deseja desativar este usuário? Ele não poderá mais acessar o sistema.",
      confirmText: "Desativar",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/users/${usuarioId}`);
      toast.success("Usuário desativado com sucesso!");
      fetchData();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Erro ao desativar usuário");
      }
    }
  };

  const handleActivate = async (usuarioId) => {
    try {
      await axios.put(`${API}/users/${usuarioId}/activate`);
      toast.success("Usuário reativado com sucesso!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao reativar usuário");
    }
  };

  const handleResetSenha = async (e) => {
    e.preventDefault();
    if (!resetSenha || resetSenha.trim().length < 4) {
      toast.error("A nova senha deve ter pelo menos 4 caracteres");
      return;
    }
    setResetLoading(true);
    try {
      await axios.put(`${API}/users/${resetUser.id}/password`, { nova_senha: resetSenha });
      toast.success(`Senha de ${resetUser.nome} redefinida com sucesso!`);
      setResetUser(null);
      setResetSenha('');
    } catch (error) {
      toast.error("Erro ao redefinir senha");
    } finally {
      setResetLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    // Se for data com horário (ISO string)
    if (dateString.includes('T')) {
      return new Date(dateString).toLocaleString('pt-BR');
    }
    // Se for apenas data (YYYY-MM-DD)
    const [ano, mes, dia] = dateString.split('-');
    return `${dia}/${mes}/${ano}`;
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      {confirmDialog}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Usuários</h1>
          <p className="text-muted-foreground">Gerencie os usuários do sistema e seus acessos</p>
        </div>
        <Button onClick={handleNew} data-testid="novo-usuario-btn">
          <Plus className="h-4 w-4 mr-2" />
          Novo Usuário
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex justify-between items-center">
            Lista de Usuários
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Total: {usuarios.length} usuários
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Tipo de Acesso</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Criado em</TableHead>
                <TableHead>Último Login</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usuarios.map((usuario) => (
                <TableRow key={usuario.id}>
                  <TableCell className="font-medium">{usuario.nome}</TableCell>
                  <TableCell>{usuario.email}</TableCell>
                  <TableCell>
                    <Badge variant={usuario.role === 'admin' ? 'default' : 'secondary'}>
                      {usuario.role === 'admin' ? 'Administrador' : 'Operacional'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={usuario.status === 'ativo' ? 'default' : 'destructive'}>
                      {usuario.status === 'ativo' ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(usuario.created_at)}</TableCell>
                  <TableCell>{formatDate(usuario.ultimo_login_em)}</TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(usuario)} data-testid={`editar-usuario-${usuario.id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" title="Redefinir senha" onClick={() => { setResetUser(usuario); setResetSenha(''); }} data-testid={`redefinir-senha-usuario-${usuario.id}`}>
                        <KeyRound className="h-4 w-4" />
                      </Button>
                      {usuario.status === 'ativo' ? (
                        <Button size="sm" variant="outline" onClick={() => handleDeactivate(usuario.id)} data-testid={`desativar-usuario-${usuario.id}`}>
                          <X className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => handleActivate(usuario.id)} data-testid={`ativar-usuario-${usuario.id}`}>
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId ? 'Editar Usuário' : 'Novo Usuário'}
            </DialogTitle>
            <DialogDescription>
              {editingId ? 'Atualize as informações do usuário' : 'Adicione um novo usuário ao sistema'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nome">Nome Completo</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  placeholder="Nome completo"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="email@exemplo.com"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="role">Tipo de Acesso</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo de acesso" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operacional">Operacional</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {editingId && (
              <div>
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="inativo">Inativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              )}
            </div>

            {/* Campo de senha */}
            <div>
              <Label htmlFor="nova_senha">{editingId ? 'Nova Senha (opcional)' : 'Senha *'}</Label>
              <Input
                id="nova_senha"
                type="password"
                value={formData.nova_senha}
                onChange={(e) => setFormData({...formData, nova_senha: e.target.value})}
                placeholder={editingId ? "Digite uma nova senha (deixe vazio para manter a atual)" : "Defina a senha de acesso"}
                required={!editingId}
              />
              {editingId && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Deixe em branco para manter a senha atual
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => {
                setIsDialogOpen(false);
                resetForm();
              }}>
                Cancelar
              </Button>
              <Button type="submit">
                {editingId ? 'Atualizar' : 'Criar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetUser} onOpenChange={(open) => { if (!open) { setResetUser(null); setResetSenha(''); } }}>
        <DialogContent data-testid="reset-senha-dialog">
          <DialogHeader>
            <DialogTitle>Redefinir senha</DialogTitle>
            <DialogDescription>
              Defina uma nova senha para <strong>{resetUser?.nome}</strong> ({resetUser?.email}).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetSenha} className="space-y-4">
            <div>
              <Label htmlFor="reset-nova-senha">Nova senha</Label>
              <Input
                id="reset-nova-senha"
                type="password"
                value={resetSenha}
                onChange={(e) => setResetSenha(e.target.value)}
                placeholder="Digite a nova senha"
                required
                data-testid="reset-senha-input-admin"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => { setResetUser(null); setResetSenha(''); }}>
                Cancelar
              </Button>
              <Button type="submit" disabled={resetLoading} data-testid="reset-senha-confirmar-btn">
                {resetLoading ? 'Salvando...' : 'Redefinir senha'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Usuarios;
